import { TextField, TextFieldProps } from "@mui/material";
import { fieldAccessorySlot } from "components/Forms/FieldAccessory";
import { passwordManagerIgnoreInputProps } from "components/Forms/passwordManagerIgnoreInputProps";
import { Control, FieldValues, Path } from "react-hook-form";

import { SuggestedField } from "./SuggestedField";

type SuggestedTextFieldProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  needsReview: boolean;
} & Omit<TextFieldProps, "name" | "value" | "onChange" | "onBlur">;

/**
 * Convenience wrapper around SuggestedField for plain TextFields.
 * For custom inputs (checkboxes, numeric fields), use SuggestedField directly.
 */
export function SuggestedTextField<T extends FieldValues>({
  name,
  control,
  needsReview,
  ...textFieldProps
}: SuggestedTextFieldProps<T>) {
  return (
    <SuggestedField
      name={name}
      control={control}
      needsReview={needsReview}
      label={textFieldProps.label}
    >
      {({ field, fieldState, isSuggested, sparkleAdornment, accessory }) => {
        // Surface react-hook-form validation errors (e.g. from useValidatingForm)
        // on the field. A field error takes precedence over any static
        // helperText hint; an explicitly passed `error` still forces the state.
        const slot = fieldAccessorySlot(
          accessory,
          fieldState.error?.message ?? textFieldProps.helperText
        );
        return (
          <TextField
            {...field}
            {...textFieldProps}
            error={!!fieldState.error || textFieldProps.error}
            helperText={slot.helperText}
            slotProps={{
              ...textFieldProps.slotProps,
              formHelperText: {
                ...slot.formHelperText,
                ...textFieldProps.slotProps?.formHelperText,
              },
              input: {
                ...textFieldProps.slotProps?.input,
                startAdornment: isSuggested ? sparkleAdornment : undefined,
              },
              htmlInput: {
                ...passwordManagerIgnoreInputProps,
                ...textFieldProps.slotProps?.htmlInput,
              },
            }}
          />
        );
      }}
    </SuggestedField>
  );
}
